import { PROCESS_AUTOMATION_SERVICE_URL } from "../Common/Constant/API";
import { commonRequestHeaders } from "../Common/request/headers";

const DEFAULT_AGENT_ENDPOINT = "/api/v1/ai-mode/chat";

export const getAIAgentEndpoint = () => {
  const env = process.env.REACT_APP_AI_AGENT_URL;
  if (env) {
    return env;
  }
  return `${PROCESS_AUTOMATION_SERVICE_URL}${DEFAULT_AGENT_ENDPOINT}`;
};

/**
 * Sends a message to the AI agent and returns the response.
 *
 * @param {Object} params
 * @param {string} params.message - The user message
 * @param {Array} params.history - Chat history (unused by backend but kept for API compat)
 * @param {File[]} params.attachments - File attachments
 * @param {(reply: string) => void} params.onChunk - Called with the full reply once received
 * @returns {Promise<string>} The full reply
 */
export const sendAIAgentMessage = async ({ message, history = [], attachments = [], onChunk }) => {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("message", message);
  attachments.forEach((attachment) => {
    formData.append("attachments", attachment, attachment.name);
  });

  const headers = {
    ...commonRequestHeaders(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const response = await fetch(getAIAgentEndpoint(), {
    method: "POST",
    headers,
    body: formData,
  });

  if (!response.ok) {
    let errorMessage;
    try {
      const errorBody = await response.text();
      try {
        const parsed = JSON.parse(errorBody);
        errorMessage = parsed.error || parsed.message || parsed.response || errorBody;
      } catch {
        errorMessage = errorBody;
      }
    } catch {
      errorMessage = `Agent request failed with status ${response.status}.`;
    }
    throw new Error(errorMessage);
  }

  const responseText = await response.text();
  let fullReply = responseText;

  // Try to extract the message if the response is JSON
  if (fullReply) {
    try {
      const parsed = JSON.parse(fullReply);
      fullReply = parsed.response || parsed.message || parsed.reply || fullReply;
    } catch {
      // Response is plain text — use as-is
    }
  }

  if (!fullReply) {
    fullReply = "The agent returned an empty response.";
  }

  if (onChunk) onChunk(fullReply);

  return fullReply;
};